import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import * as imageCompression from "browser-image-compression";
import { Subject } from "rxjs";
import { MediaTrackConstraintSetEx } from "./overriden-models";
@Component({
  selector: "app-camera",
  templateUrl: "./camera.component.html",
  styleUrls: ["./camera.component.scss"],
})
export class CameraComponent implements OnInit {
  @ViewChild("stream") videoRef: ElementRef<HTMLVideoElement>;
  @ViewChild("canvas") canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild("zoomSlider") zoomSliderRef: ElementRef<HTMLInputElement>;
  video: HTMLVideoElement;
  canvas: HTMLCanvasElement;
  onSnap: Subject<File | void> = new Subject<File | void>();
  onClose: Subject<void> = new Subject<void>();
  track: MediaStreamTrack;
  width = 0;
  height = 0;
  streaming: boolean = false;
  hasCamera: boolean = false;
  maxSizeInKb: number = -1;
  cameraCapabilities: MediaTrackCapabilities;
  cameraSettings: MediaTrackSettings;
  deviceId: string;
  torchOn = false;
  zoomValue: number;
  zoomMax = 5;
  zoomMin = 1;
  zoomStep = 0.1;
  EventListenersAdded = false;
  constructor() {}

  ngOnInit() {
    if ("mediaDevices" in navigator)
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices: MediaDeviceInfo[]) => {
          devices.forEach((device: MediaDeviceInfo) => {
            if (device.kind === "videoinput") {
              this.hasCamera = true;
              this.deviceId = device.deviceId;
              return;
            }
          });
        })
        .catch((err) => {
          console.log(err.name + ": " + err.message);
        });
    else this.hasCamera = false;
  }

  start() {
    this.video = this.videoRef.nativeElement;
    if (!this.EventListenersAdded) {
      this.canvas = this.canvasRef.nativeElement;
      this.video.addEventListener(
        "touchmove",
        function (e) {
          e.preventDefault();
        },
        { passive: false }
      );
      this.video.addEventListener(
        "canplay",
        () => {
          if (!this.streaming) {
            this.height = this.video.videoHeight;
            this.width = this.video.videoWidth;
            this.canvas.width = this.width;
            this.canvas.height = this.height;
            this.video.setAttribute("width", this.width.toString());
            this.video.setAttribute("height", this.height.toString());
            this.canvas.setAttribute("width", this.width.toString());
            this.canvas.setAttribute("height", this.height.toString());
            this.streaming = true;
          }
        },
        { passive: true }
      );
      this.EventListenersAdded = true;
    }

    let success = (stream: MediaStream) => {
      this.video.srcObject = stream;
      this.video.play();
      this.track = stream.getVideoTracks()[0];
      this.handleZoom(stream);
    };

    this.startVideoStream(
      {
        video: {
          deviceId: this.deviceId,
          zoom: true,
          focusMode: "continuous",
          facingMode: { exact: "environment" },
          aspectRatio: 3264 / 2448,
          width: { ideal: 3264 },
          height: { ideal: 2448 },
        } as MediaTrackConstraintSetEx,
        audio: false,
      },
      success,
      (err: any) => {
        if (
          err instanceof OverconstrainedError &&
          err.constraint == "facingMode"
        )
          // fallback to front-camera (or any other camera)
          this.startVideoStream({ video: true, audio: false }, success, (err) =>
            console.log("An error occurred: " + err)
          );
        else console.log("An error occurred: " + err);
      }
    );
  }
  toggleFlashlight() {
    this.torchOn = !this.torchOn;
    this.track.applyConstraints({
      advanced: [
        {
          torch: this.torchOn,
        } as MediaTrackConstraintSetEx,
      ],
    });
  }

  handleZoom(stream: MediaStream) {
    this.track = stream.getVideoTracks()[0];
    this.cameraCapabilities =
      this.track.getCapabilities() as MediaTrackCapabilities;
    this.cameraSettings = this.track.getSettings();
    if (!("zoom" in this.cameraSettings)) return;
    this.zoomValue = this.cameraCapabilities["zoom"].min;
    this.setZoomSlider();
    this.track.applyConstraints({
      advanced: [
        {
          zoom: this.zoomValue,
        } as MediaTrackConstraintSetEx,
      ],
    });
  }

  setZoomSlider() {
    this.zoomMin = this.cameraCapabilities["zoom"].min;
    this.zoomMax = this.cameraCapabilities["zoom"].max;
    this.zoomStep = this.cameraCapabilities["zoom"].step;
  }
  onPinch(event) {
    // event.preventDefault();  DOES NOT WORK SO WE NEED TO HAVE A SECOND EVENT LISTERER TO PREVENT DEFAULT
    if (
      event.additionalEvent == "pinchin" &&
      this.zoomValue > this.cameraCapabilities["zoom"].min
    )
      this.zoomValue = this.zoomValue - this.cameraCapabilities["zoom"].step;
    else if (
      event.additionalEvent == "pinchout" &&
      this.zoomValue < this.cameraCapabilities["zoom"].max
    )
      this.zoomValue = this.zoomValue + this.cameraCapabilities["zoom"].step;
    else return;

    this.track.applyConstraints({
      advanced: [
        {
          zoom: this.zoomValue,
        } as MediaTrackConstraintSetEx,
      ],
    });
  }

  onSlide(event) {
    this.zoomValue = (event.srcElement as HTMLInputElement).valueAsNumber;
    this.track.applyConstraints({
      advanced: [
        {
          zoom: this.zoomValue,
        } as MediaTrackConstraintSetEx,
      ],
    });
  }

  snap() {
    this.height = this.video.videoHeight;
    this.width = this.video.videoWidth;
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.canvas
      .getContext("2d")
      .drawImage(this.video, 0, 0, this.width, this.height);
    this.stop();
    // alert("1: BEFORE CANVASTOBLOB");
    this.getCanvasBlob(this.canvas).then((file) => this.emitFile(file));
  }

  getCanvasBlob(canvas: HTMLCanvasElement): Promise<File> {
    return imageCompression.default.canvasToFile(
      canvas,
      "image/jpeg",
      "modified",
      Date.now()
    );
  }

  close() {
    this.torchOn = false;
    this.onSnap.next();
    this.stop();
  }

  stop() {
    const memStream = this.video.srcObject as MediaStream;
    memStream &&
      memStream.getVideoTracks().forEach((track) => {
        track.stop();
      });
    this.streaming = false;
  }

  private startVideoStream(
    constraints: MediaStreamConstraints,
    successCallback: (stream: MediaStream) => void,
    errorCallback: (err: any) => void
  ) {
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(successCallback)
      .catch(errorCallback);
  }

  private emitFile(file: File) {
    this.torchOn = false;
    this.stop();
    this.onSnap.next(file);
  }
}
export interface OverconstrainedError extends Error {
  constraint: string;
}

declare var OverconstrainedError: {
  prototype: OverconstrainedError;
  new (): OverconstrainedError;
};
